'use client'

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { IRestaurantReward } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { noOp } from '@/utils/functions/noOp'
import { rgba } from '@/utils/styles/rgba'

interface IRestaurantRewardCardProps {
    reward: IRestaurantReward
}

export const RestaurantRewardCard = ({ reward }: IRestaurantRewardCardProps) => {
    const { restaurant, restaurantSlug, citySlug } = useRestaurantDetailContext()

    const t = useTranslations()

    const link = useMemo(() => {
        if (!restaurant) return

        return `https://app.adjust.com/147kxpz8?campaign=RestaurantDetail&adgroup=${restaurantSlug}-${citySlug}&engagement_type=fallback_click`
    }, [restaurant, restaurantSlug, citySlug])

    return (
        <div
            className="rounded-l bg-grey-100 py-4 w-full max-w-full md:max-w-[547px] shadow-soft justify-between
                min-h-full flex flex-col"
        >
            <div className="w-full overflow-hidden">
                <h3 className="text-[20px] md:text-[24px] font-bold mb-3.5 px-5 ">{reward.name}</h3>

                <div className="relative">
                    <div className="flex flex-row gap-1 mb-3.5 px-5 overflow-y-hidden overflow-x-scroll scrollbar-hide relative">
                        <RestaurantRewardCardTag
                            label={t('restaurants.detail.rewards.checkins', {
                                days: reward.requiredCheckIns ?? '',
                            })}
                        />
                    </div>
                </div>

                <p className="text-gray-700 mb-3.5 md:text-[16px] text-[14px] px-5">
                    {reward.description}
                </p>
            </div>

            <div className="px-5 w-full">
                <Button
                    {...(link ? { href: { externalUrl: link } } : { onClick: noOp })}
                    styleType="primary"
                    shape="pill"
                    borderColor="rgba(0, 0, 0, 0.1)"
                    backgroundColor="#1F4F3E"
                    contentColor="white"
                    height="52px"
                    width="100%"
                >
                    {t('restaurants.detail.rewards.cta')}
                </Button>
            </div>
        </div>
    )
}

interface IRestaurantRewardCardTagProps {
    icon?: React.ReactNode
    label: string
}

const RestaurantRewardCardTag = ({ icon, label }: IRestaurantRewardCardTagProps) => {
    return (
        <div
            className="rounded-full min-h-[32px] max-h-[32px] flex items-center justify-center w-fit gap-x-1 px-3 bg-mustard-200"
            style={{
                border: `2px solid ${rgba(0, 0, 0, 0.1)}`,
            }}
        >
            {icon && <span>{icon}</span>}

            <p className="font-bold text-[13px] whitespace-nowrap">{label}</p>
        </div>
    )
}
